import React, { useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useFormContext } from "react-hook-form";
import { Title } from "@/types"; // Import the Title enum
import { getFieldError } from "@/utils";

interface ContactFormItemProps {
  prefix: string;
  isOwner?: boolean;
  jointMemberCount?: number;
  maxJointMembers?: number;
  isExplorer?: boolean;
}

export const ContactFormItem = React.forwardRef<HTMLDivElement, ContactFormItemProps>(
  ({ prefix, isOwner = false, jointMemberCount = 0, maxJointMembers = 4, isExplorer = false }, ref) => {
    const { register, setValue, watch, formState: { errors } } = useFormContext();
    const watchedTitle = watch(`${prefix}.title`); // Correctly initialized
    const watchedData = watch(prefix); // Correctly initialized
    const watchedEmail = watch(`${prefix}.email`); // Correctly initialized
    const watchedIsJoint = watch(`${prefix}.isJointMember`); // NEW: track checkbox value

    // Check if joint member limit is reached (only prevent if not already checked)
    const isJointLimitReached = !watchedIsJoint && jointMemberCount >= maxJointMembers;

    // Set the title from form data when component mounts or when data changes
    useEffect(() => {
      if (watchedData?.title && !watchedTitle) {
        setValue(`${prefix}.title`, watchedData.title);
      }

      // Sync isJointMember from existing data (edit mode)
      if (typeof watchedData?.isJointMember !== "undefined") {
        setValue(`${prefix}.isJointMember`, Boolean(watchedData.isJointMember));
      }


    }, [watchedData, watchedTitle, prefix, setValue, watchedEmail]);

    return (
      <div ref={ref} className="space-y-4">
        {/* Title and name fields in one row */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="w-[80px]">
            <Label htmlFor={`${prefix}.title`}>Title *</Label>
            <Select
              value={watchedTitle || ""}
              onValueChange={(value) => setValue(`${prefix}.title`, value, { shouldValidate: true })}
              required
            >
              <SelectTrigger
                className={`w-full h-10 ${getFieldError(errors, `${prefix}.title`) ? "border-destructive ring-destructive" : ""}`}
              >
                <SelectValue placeholder="Title" />
              </SelectTrigger>
              <SelectContent>
                {/* Use the Title enum instead of hardcoded values */}
                {Object.values(Title).map((title) => (
                  <SelectItem key={title} value={title}>{title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              {...register(`${prefix}.title`, { required: true })}
              type="hidden"
            />
          </div>

          <div className="flex-1 min-w-[120px]">
            <Label htmlFor={`${prefix}.firstName`}>First Name *</Label>
            <Input
              id={`${prefix}.firstName`}
              {...register(`${prefix}.firstName`, { required: true })}
              className={getFieldError(errors, `${prefix}.firstName`) ? "border-destructive ring-destructive" : ""}
            />
          </div>

          <div className="flex-1 min-w-[120px]">
            <Label htmlFor={`${prefix}.middleName`}>Middle Name</Label>
            <Input
              id={`${prefix}.middleName`}
              {...register(`${prefix}.middleName`)}
            />
          </div>

          <div className="flex-1 min-w-[120px]">
            <Label htmlFor={`${prefix}.lastName`}>Last Name *</Label>
            <Input
              id={`${prefix}.lastName`}
              {...register(`${prefix}.lastName`, { required: true })}
              className={getFieldError(errors, `${prefix}.lastName`) ? "border-destructive ring-destructive" : ""}
            />
          </div>
        </div>

        {/* Email and Phone fields in one row for owner */}
        <div className="flex flex-wrap gap-3">
          <div className={`${isOwner ? 'flex-1' : 'w-full'}`}>
            <Label htmlFor={`${prefix}.email`}>Email *</Label>
            {!isOwner ? (
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <Input
                    id={`${prefix}.email`}
                    type="email"
                    {...register(`${prefix}.email`, {
                      required: true,
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address"
                      }
                    })}
                    defaultValue={watchedData?.email || ''}
                    className={getFieldError(errors, `${prefix}.email`) ? "border-destructive ring-destructive" : ""}
                  />
                </div>
                {!isExplorer && (
                  <div className="flex items-center space-x-2 pb-1">
                    <Checkbox
                      id={`${prefix}.isJointMember`}
                      checked={Boolean(watchedIsJoint)}
                      onCheckedChange={(checked) => {
                        // Prevent checking if joint member limit is reached
                        if (checked && isJointLimitReached) {
                          return;
                        }
                        setValue(`${prefix}.isJointMember`, checked === true, { shouldDirty: true });
                      }}
                      disabled={isJointLimitReached}
                    />
                    <Label htmlFor={`${prefix}.isJointMember`} className={`text-sm font-normal ${isJointLimitReached ? 'text-muted-foreground' : ''}`}>
                      Joint Member
                    </Label>
                    {/* Register the field so RHF tracks default/reset values */}
                    <input type="hidden" {...register(`${prefix}.isJointMember`)} />
                  </div>
                )}
                {!isExplorer && isJointLimitReached && (
                  <p className="text-xs text-amber-600 mt-1">
                    Maximum of {maxJointMembers} joint members allowed
                  </p>
                )}
              </div>
            ) : (
              <Input
                id={`${prefix}.email`}
                type="email"
                {...register(`${prefix}.email`, {
                  required: true,
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address"
                  }
                })}
                defaultValue={watchedData?.email || ''}
                className={getFieldError(errors, `${prefix}.email`) ? "border-destructive ring-destructive" : ""}
              />
            )}
          </div>

          {/* Phone field - only show for owner */}
          {isOwner && (
            <div className="w-1/3">
              <Label htmlFor={`${prefix}.phone`}>Phone Number</Label>
              <Input
                id={`${prefix}.phone`}
                type="tel"
                {...register(`${prefix}.phone`, {
                  pattern: {
                    value: /^\+[1-9]\d{9,14}$/, // Updated to enforce at least 10 digits
                    message: "Phone number must be in the format +CountryCode PhoneNumber (e.g., +14155552671)"
                  }
                })}
                placeholder="+14155552671"
                className={getFieldError(errors, `${prefix}.phone`) ? "border-destructive ring-destructive" : ""}
              />
              {getFieldError(errors, `${prefix}.phone`) && (
                <p className="text-sm text-destructive mt-1">
                  {getFieldError(errors, `${prefix}.phone`)?.message as string}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);

ContactFormItem.displayName = "ContactFormItem";